import { createContext, useContext, useState, type ReactNode } from "react";
import { placeRole, type Place } from "@teamna/web-client/lib/places.ts";
import { Avatar } from "../components/Avatar.tsx";
import { BusinessMark } from "../components/BusinessMark.tsx";
import { Sheet } from "../components/Sheet.tsx";

/**
 * The places one account can open, drawn three ways (prototype
 * `teamna/prototypes/account-switch.html`): big cards on "Where do you want to go?", rows in the
 * office account menu, and rows in the phone sheet behind the "Switch" button.
 */

/** The cards of the question screen. The place this device opened last has a "Last time" tag. */
export function PlaceCards({
  places,
  lastId,
  disabled = false,
  busyId = null,
  onPick,
}: {
  places: Place[];
  lastId: string | null;
  disabled?: boolean;
  /** The place being opened (the name is saving): its card shows that, and every card waits. */
  busyId?: string | null;
  onPick: (place: Place) => void;
}) {
  return (
    <ul className="m-0 flex list-none flex-col gap-2 p-0">
      {places.map((place) => (
        <li key={place.id}>
          <button
            type="button"
            disabled={disabled || busyId !== null}
            onClick={() => onPick(place)}
            className="grid w-full grid-cols-[40px_minmax(0,1fr)_auto] items-center gap-3 rounded-card border-[1.5px] border-line bg-paper px-4 py-3 text-left transition-colors hover:border-line-strong disabled:cursor-not-allowed disabled:opacity-55"
          >
            <BusinessMark business={place.business} size={40} />
            <span className="min-w-0">
              <span className="flex flex-wrap items-center gap-x-2">
                <b className="truncate text-[15.5px] text-ink">{place.business.name}</b>
                {place.id === lastId ? (
                  <span className="rounded-pill bg-confirmed-surface px-2 py-0.5 text-[11px] font-bold text-confirmed">Last time</span>
                ) : null}
              </span>
              <span className="block text-[13px] text-ink-2">{busyId === place.id ? "Opening..." : placeRole(place)}</span>
            </span>
            <span aria-hidden="true" className="text-[18px] text-ink-3">
              ›
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

/** Compact rows for a menu: the place you are in says "Here". */
export function PlaceRows({ places, currentId, onPick }: { places: Place[]; currentId: string | null; onPick: (place: Place) => void }) {
  return (
    <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
      {places.map((place) => {
        const here = place.id === currentId;
        return (
          <li key={place.id}>
            <button
              type="button"
              aria-current={here ? "true" : undefined}
              onClick={() => onPick(place)}
              className="grid w-full grid-cols-[30px_minmax(0,1fr)_auto] items-center gap-2.5 rounded-lg px-2 py-2 text-left hover:bg-surface"
            >
              <BusinessMark business={place.business} size={30} />
              <span className="min-w-0">
                <span className="block truncate text-[14px] font-semibold text-ink">{place.business.name}</span>
                <span className="block truncate text-[12px] text-ink-2">{placeRole(place)}</span>
              </span>
              {here ? <span className="text-[11.5px] font-bold text-accent-strong">Here</span> : null}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export type PlaceSwitch = {
  places: Place[];
  currentId: string | null;
  userName: string;
  photoUrl?: string | null;
  onPick: (place: Place) => void;
};

const PlaceSwitchContext = createContext<PlaceSwitch | null>(null);

/** The member and staff layouts provide this; `Screen` reads it to show the "Switch" button. */
export function PlaceSwitchProvider({ value, children }: { value: PlaceSwitch | null; children: ReactNode }) {
  return <PlaceSwitchContext.Provider value={value}>{children}</PlaceSwitchContext.Provider>;
}

export function usePlaceSwitch(): PlaceSwitch | null {
  return useContext(PlaceSwitchContext);
}

/**
 * The phone switch: a labelled button (a bare icon in this corner once signed people out by
 * mistake, see `SignOutRow`), and a sheet with the places. Only for an account with two or more.
 */
export function PhonePlaceSwitch() {
  const value = usePlaceSwitch();
  const [open, setOpen] = useState(false);
  if (!value || value.places.length < 2) return null;
  const current = value.places.find((place) => place.id === value.currentId) ?? null;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={current ? `Switch. Now: ${current.business.name}, ${placeRole(current)}` : "Switch"}
        className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-pill border border-line bg-paper pr-3 pl-1 text-[13px] font-semibold text-ink-2 hover:border-line-strong"
      >
        <Avatar name={value.userName} photoUrl={value.photoUrl ?? null} size={28} />
        Switch
      </button>
      <Sheet open={open} onClose={() => setOpen(false)} title="Switch to" subtitle={value.userName}>
        <PlaceRows
          places={value.places}
          currentId={value.currentId}
          onPick={(place) => {
            setOpen(false);
            if (place.id !== value.currentId) value.onPick(place);
          }}
        />
      </Sheet>
    </>
  );
}
